import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { FileInput, parseFile, PARSER_ERROR } from 'csv-file-parser-lib';
import { addNotification, clearNotification } from '../../states/notifications/actions';
import { getAllNotifications } from '../../states/notifications/selectors';
import { getContentKey } from '../../utils/benImportUtils';
import {
	getErrorKey,
	getDataIntoObject,
	getImportedPaymentsList,
	checkEmptySortCodeOrAccountNumber,
} from '../../utils/paymentsImportUtils';
import { TYPE_FASTER_PAYMENTS, TYPE_CHAPS } from '../../config';
import { CONST_FILE_UPLOAD } from '../../config/constants';

const mapState = (state) => ({
	notification: getAllNotifications(state),
});

const mapDispatch = (dispatch) => ({
	addNotification: (key, params) => dispatch(addNotification(key, params)),
	clearNotification: (id) => dispatch(clearNotification(id)),
});

export const PaymentsFileImport = ({
	addNotification,
	notification,
	clearNotification,
	handleChooseFileButtonClick,
	isHeaderRow,
	maxRecords,
	rules,
	type,
	getRules,
}) => {
	/**
	 * @param {Object} error Error object if any
	 * @param {String} errorKey Error key if any
	 * @param {Array} importedPaymentsList The formatted data from file
	 * @param {String} fileName The file name
	 * @param {Boolean} hasError If any error before continue
	 * @returns {void}
	 */
	const handleContinue = (error = {}, errorKey = '', importedPaymentsList = [], fileName = '', hasError = true) => {
		const formattedData = [];
		if (error.rowData && error.rowData.length > 0) {
			formattedData.push(getDataIntoObject(error.rowData, type));
		}
		if (hasError) {
			addNotification(errorKey, {
				formattedData,
				contentKey: getContentKey(error.type),
				errorType: error.type,
				maxRecordsLimit: maxRecords,
				parserErrorMsg: error.message,
			});
		}
		handleChooseFileButtonClick(importedPaymentsList, fileName, hasError);
	};

	/**
	 * @param {*} results The results
	 * @param {File} file The file
	 * @param {Boolean} hasError If any error before continue
	 * @param {Object} error Error object if any
	 * @param {String} errorKey Error key if any
	 * @returns {void}
	 */
	const onParseComplete = (results, file, hasError = false, error = {}, errorKey = '') => {
		const notificationId = get(notification, '[0].id');
		if (notificationId) clearNotification(notificationId);

		if ([TYPE_CHAPS, TYPE_FASTER_PAYMENTS].includes(type) && checkEmptySortCodeOrAccountNumber(results[0])) {
			const error = {
				rowData: get(results, '[0]'),
				type: PARSER_ERROR,
			};
			handleContinue(error, getErrorKey(type));
		} else {
			const importedPaymentsList = getImportedPaymentsList(results, type);
			handleContinue(error, errorKey, importedPaymentsList, file.name, hasError);
		}
	};
	const onError = (error) => {
		window.scrollTo(0, 0);

		const notificationId = get(notification, '[0].id');
		if (notificationId) clearNotification(notificationId);

		let errorKey = getErrorKey(type);
		if (error.length === 0) {
			const {
				CLIENT_SIDE_ERROR: { EMPTY_FILE },
			} = CONST_FILE_UPLOAD;
			errorKey = EMPTY_FILE;
		}
		handleContinue(error, errorKey);
	};
	if (getRules) {
		return (
			<FileInput
				showFilename
				id="file-input"
				onChange={(files) =>
					files &&
					parseFile(files[0], {
						ignoreEmpty: true,
						ignoreExtraColumns: true,
						minRecords: 1,
						maxRecords,
						onComplete: onParseComplete,
						onError,
						getRules,
					})
				}
			/>
		);
	}
	return (
		<FileInput
			showFilename
			id="file-input"
			onChange={(files) =>
				files &&
				parseFile(files[0], {
					isHeaderRow,
					ignoreEmpty: true,
					ignoreExtraColumns: true,
					minRecords: 1,
					maxRecords,
					onComplete: onParseComplete,
					onError,
					rules,
				})
			}
		/>
	);
};

PaymentsFileImport.propTypes = {
	addNotification: PropTypes.func.isRequired,
	clearNotification: PropTypes.func.isRequired,
	notification: PropTypes.arrayOf(PropTypes.string.isRequired),
	handleChooseFileButtonClick: PropTypes.func.isRequired,
	isHeaderRow: PropTypes.func,
	rules: PropTypes.arrayOf(PropTypes.object),
	type: PropTypes.string.isRequired,
	maxRecords: PropTypes.number.isRequired,
	getRules: PropTypes.func,
};

export default connect(mapState, mapDispatch)(PaymentsFileImport);
